import { useMutation } from "@tanstack/react-query";
import {
  Check,
  CircleX,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Pencil,
  RefreshCw,
  Save,
  ShieldCheck,
} from "lucide-react";
import {
  Form,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useLocales,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useEffect, useState } from "react";
import { useFormState } from "react-hook-form";
import { getSupabaseUrl } from "../providers/supabase/supabase";
import { RecordField } from "@/components/admin/record-field";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";

import ImageEditorField from "../misc/ImageEditorField";
import type { CrmDataProvider } from "../providers/types";
import type { Sale, SalesFormData } from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  useOptionalCRM,
  ACTIVE_CRM_STORAGE_KEY,
  DEFAULT_CRM_ID,
} from "../context/CRMContext";
import { Switch } from "@/components/ui/switch";
import { Calendar, CalendarCheck, CheckCheck, XCircle } from "lucide-react";
import {
  GCAL_SYNC_ACTIVITY_OPTIONS,
  ALL_GCAL_SYNC_TASK_TYPES,
  getGCalSyncTaskTypes,
  setGCalSyncTaskTypes,
  getConnectedGoogleUser,
  connectGoogleOAuth,
  disconnectGoogleUser,
  type GoogleUser,
} from "../providers/google/googleIntegrationService";
import { ACTIVITY_TYPES } from "../agenda/activityTypes";

export const ProfilePage = () => {
  const [isEditMode, setEditMode] = useState(false);
  const { identity, refetch: refetchIdentity } = useGetIdentity();
  const { data, refetch: refetchUser } = useGetOne("sales", {
    id: identity?.id,
  });
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      if (!identity) {
        throw new Error(
          translate("crm.profile.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      return dataProvider.salesUpdate(identity.id, data);
    },
    onSuccess: () => {
      refetchIdentity();
      refetchUser();
      setEditMode(false);
      notify("crm.profile.updated", {
        messageArgs: {
          _: "Your profile has been updated",
        },
      });
    },
    onError: (_) => {
      notify("crm.profile.update_error", {
        type: "error",
        messageArgs: {
          _: "An error occurred. Please try again",
        },
      });
    },
  });

  if (!identity) return null;

  const handleOnSubmit = async (values: any) => {
    mutate(values);
  };

  return (
    <div className="max-w-lg mx-auto mt-8">
      <Form onSubmit={handleOnSubmit} record={data}>
        <ProfileForm isEditMode={isEditMode} setEditMode={setEditMode} />
      </Form>
    </div>
  );
};

const ProfileForm = ({
  isEditMode,
  setEditMode,
}: {
  isEditMode: boolean;
  setEditMode: (value: boolean) => void;
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const record = useRecordContext<Sale>();
  const { identity, refetch } = useGetIdentity();
  const { isDirty } = useFormState();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate: mutateSale } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      if (!record) {
        throw new Error(
          translate("crm.profile.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      return dataProvider.salesUpdate(record.id, data);
    },
    onSuccess: () => {
      refetch();
      notify("crm.profile.updated", {
        messageArgs: {
          _: "Your profile has been updated",
        },
      });
    },
    onError: () => {
      notify("crm.profile.update_error", {
        type: "error",
        messageArgs: {
          _: "An error occurred. Please try again.",
        },
      });
    },
  });

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  if (!identity) return null;

  const handleSaveNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      notify("A senha deve ter no mínimo 6 caracteres.", { type: "warning" });
      return;
    }
    if (newPassword !== confirmPassword) {
      notify("As senhas digitadas não coincidem.", { type: "warning" });
      return;
    }

    setSavingPassword(true);
    try {
      const client = getSupabaseClient();
      if (client) {
        const { error } = await client.auth.updateUser({
          password: newPassword,
        });
        if (error) throw error;
      }
      if (record?.id) {
        await dataProvider.salesUpdate(record.id, {
          password: newPassword,
        } as any);
      }
      notify("Senha alterada com sucesso!", { type: "success" });
      setIsPasswordModalOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      console.error("Erro ao alterar senha:", err);
      notify(err?.message || "Não foi possível alterar a senha.", {
        type: "error",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarUpdate = async (values: any) => {
    mutateSale(values);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent>
          <div className="mb-4 flex flex-row justify-between">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.profile.title")}
            </h2>
          </div>

          <div className="space-y-4 mb-4">
            <ImageEditorField
              source="avatar"
              type="avatar"
              onSave={handleAvatarUpdate}
              linkPosition="right"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextRender source="first_name" isEditMode={isEditMode} />
              <TextRender source="last_name" isEditMode={isEditMode} />
            </div>
            <TextRender source="email" isEditMode={isEditMode} />
            <TextRender
              source="dotus_instance_id"
              label="Estação WhatsApp Web (Dotus WA Call)"
              helperText={
                isEditMode
                  ? "Identificador da instância configurada no app do operador (ex: comercial-joao)"
                  : false
              }
              isEditMode={isEditMode}
            />
            <LanguageSelector />
          </div>

          <div className="flex flex-row justify-end gap-2">
            {!isEditMode && (
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsPasswordModalOpen(true)}
              >
                {translate("crm.profile.password.change")}
              </Button>
            )}

            <Button
              type="button"
              variant={isEditMode ? "ghost" : "outline"}
              onClick={() => setEditMode(!isEditMode)}
              className="flex items-center"
            >
              {isEditMode ? <CircleX /> : <Pencil />}
              {isEditMode
                ? translate("ra.action.cancel")
                : translate("ra.action.edit")}
            </Button>

            {isEditMode && (
              <Button type="submit" disabled={!isDirty} variant="outline">
                <Save />
                {translate("ra.action.save")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal para Alterar Senha Diretamente */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Alterar Minha Senha
            </DialogTitle>
            <DialogDescription className="text-xs">
              Digite e confirme sua nova senha de acesso ao CRM DOTUS.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Nova Senha</Label>
              <Input
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Confirmar Nova Senha
              </Label>
              <Input
                type="password"
                placeholder="Repita a nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={savingPassword}
              onClick={handleSaveNewPassword}
            >
              {savingPassword ? "Salvando..." : "Salvar Senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sincronização de Atividades com Google Agenda */}
      <GoogleCalendarSyncCard />

      {import.meta.env.VITE_INBOUND_EMAIL && (
        <Card>
          <CardContent>
            <div className="space-y-4 justify-between">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("crm.profile.inbound.title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {translate("crm.profile.inbound.description", {
                  _: "You can start sending emails to your server's inbound email address, e.g. by adding it to the Cc: field. Atomic CRM will process the emails and add notes to the corresponding contacts.",
                  field: "Cc:",
                })}
              </p>
              <CopyPaste value={import.meta.env.VITE_INBOUND_EMAIL} />
            </div>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent>
          <div className="space-y-4 justify-between">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.profile.mcp.title", {
                _: "MCP Server",
              })}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate("crm.profile.mcp.description", {
                _: "Use this URL to connect your AI assistant to your CRM data via the Model Context Protocol (MCP).",
              })}
            </p>
            <CopyPaste value={`${getSupabaseUrl()}/functions/v1/mcp`} />
          </div>
        </CardContent>
      </Card>
      <ApiKeyCard />
    </div>
  );
};

const LanguageSelector = () => {
  const translate = useTranslate();
  const locales = useLocales();
  const [locale, setLocale] = useLocaleState();

  if (locales.length <= 1) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {translate("crm.language")}
      </p>
      <Select value={locale} onValueChange={setLocale}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {locales.map((language) => (
            <SelectItem key={language.locale} value={language.locale}>
              {language.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const TextRender = ({
  source,
  isEditMode,
  className,
  label,
  helperText,
}: {
  source: string;
  isEditMode: boolean;
  className?: string;
  label?: string;
  helperText?: string | boolean;
}) => {
  const displayLabel = label || `resources.sales.fields.${source}`;
  if (isEditMode) {
    return (
      <TextInput
        source={source}
        label={displayLabel}
        helperText={helperText ?? false}
        className={className}
      />
    );
  }
  return (
    <div className={className}>
      <RecordField source={source} label={displayLabel} />
    </div>
  );
};

const CopyPaste = ({ value }: { value: string }) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(value);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            onClick={handleCopy}
            variant="ghost"
            className="normal-case justify-between w-full"
          >
            <span className="overflow-hidden text-ellipsis">{value}</span>
            {copied ? (
              <Check className="h-4 w-4 ml-2" />
            ) : (
              <Copy className="h-4 w-4 ml-2" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {copied
              ? translate("crm.common.copied")
              : translate("crm.common.copy")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ─── API Key Card ─────────────────────────────────────────────────────────────

/** Generates a random URL-safe key string with a prefix */
function generateRawKey(): string {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  const base = btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
  return `crm_${base}`;
}

/** SHA-256 hex digest (Web Crypto) */
async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── Google Calendar Activity Sync Card ───────────────────────────────────────

const GoogleCalendarSyncCard = () => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const salesId = identity?.id;

  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    getGCalSyncTaskTypes(salesId),
  );
  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(() =>
    getConnectedGoogleUser(salesId),
  );
  const [connecting, setConnecting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  useEffect(() => {
    if (salesId != null) {
      setSelectedTypes(getGCalSyncTaskTypes(salesId));
      setGoogleUser(getConnectedGoogleUser(salesId));
    }
  }, [salesId]);

  const handleToggle = (typeValue: string) => {
    const current = new Set(selectedTypes);
    if (current.has(typeValue)) {
      current.delete(typeValue);
    } else {
      current.add(typeValue);
    }
    const next = Array.from(current);
    setSelectedTypes(next);
    setGCalSyncTaskTypes(next, salesId);
    setSaveStatus("Preferência salva!");
    setTimeout(() => setSaveStatus(null), 2500);
  };

  const handleSelectAll = () => {
    const all = [...ALL_GCAL_SYNC_TASK_TYPES];
    setSelectedTypes(all);
    setGCalSyncTaskTypes(all, salesId);
    setSaveStatus("Todas as atividades marcadas!");
    setTimeout(() => setSaveStatus(null), 2500);
  };

  const handleDeselectAll = () => {
    setSelectedTypes([]);
    setGCalSyncTaskTypes([], salesId);
    setSaveStatus("Todas as atividades desmarcadas!");
    setTimeout(() => setSaveStatus(null), 2500);
  };

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      await connectGoogleOAuth(
        (user) => {
          setGoogleUser(user);
          notify("Google Agenda conectado com sucesso!", { type: "success" });
        },
        () => {
          notify("Não foi possível conectar ao Google.", { type: "error" });
        },
        salesId,
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = () => {
    if (salesId != null) {
      disconnectGoogleUser(salesId);
      setGoogleUser(null);
      notify("Conta Google desconectada.", { type: "info" });
    }
  };

  const isAllSelected =
    selectedTypes.length === ALL_GCAL_SYNC_TASK_TYPES.length;
  const isNoneSelected = selectedTypes.length === 0;

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          {/* Cabeçalho */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-500 border border-blue-500/20 shrink-0">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-muted-foreground">
                  Sincronização com Google Agenda
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Marque abaixo quais tipos de atividades criadas ou editadas no
                  CRM serão enviadas para o seu Google Calendar.
                </p>
              </div>
            </div>

            {/* Status da Conta Google */}
            <div className="flex items-center gap-2">
              {googleUser ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Conectado: {googleUser.email}</span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnectGoogle}
                    className="h-7 text-xs text-muted-foreground hover:text-destructive"
                  >
                    Desconectar
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleConnectGoogle}
                  disabled={connecting}
                  className="h-8 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-500/30 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 mr-1.5 ${connecting ? "animate-spin" : ""}`}
                  />
                  {connecting ? "Conectando..." : "Conectar Google Agenda"}
                </Button>
              )}
            </div>
          </div>

          {/* Barra de controle rápido e contador */}
          <div className="flex items-center justify-between border-y border-border/60 py-2.5 text-xs text-muted-foreground flex-wrap gap-2">
            <div className="flex items-center gap-2 font-medium">
              <CalendarCheck className="w-4 h-4 text-primary" />
              <span>
                <strong className="text-foreground">
                  {selectedTypes.length}
                </strong>{" "}
                de {ALL_GCAL_SYNC_TASK_TYPES.length} tipos sincronizando
              </span>
              {saveStatus && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-500 ml-2 animate-in fade-in duration-200">
                  <Check className="w-3.5 h-3.5" />
                  {saveStatus}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                disabled={isAllSelected}
                className="h-7 px-2 text-xs text-primary hover:text-primary"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Marcar todas
              </Button>
              <span className="text-muted-foreground/40">•</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDeselectAll}
                disabled={isNoneSelected}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Desmarcar todas
              </Button>
            </div>
          </div>

          {/* Lista com os 7 Tipos de Atividades */}
          <div className="grid grid-cols-1 gap-2 pt-1">
            {GCAL_SYNC_ACTIVITY_OPTIONS.map((opt) => {
              const isChecked = selectedTypes.includes(opt.value);
              const activityCfg = ACTIVITY_TYPES[opt.value];
              const IconComp = activityCfg?.icon || Calendar;

              return (
                <div
                  key={opt.value}
                  onClick={() => handleToggle(opt.value)}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer select-none ${
                    isChecked
                      ? "bg-card border-primary/40 shadow-xs hover:border-primary/60"
                      : "bg-muted/30 border-border/60 opacity-60 hover:opacity-90 hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`p-2 rounded-lg border shrink-0 transition-transform ${
                        activityCfg?.colorClass ||
                        "text-muted-foreground bg-muted border-border"
                      }`}
                    >
                      <IconComp className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-foreground">
                          {opt.label}
                        </span>
                        {isChecked && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                            Sincroniza com Google
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {opt.description}
                      </p>
                    </div>
                  </div>

                  <div
                    className="pl-3 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Switch
                      checked={isChecked}
                      onCheckedChange={() => handleToggle(opt.value)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-muted-foreground/80 pt-1">
            * Apenas as atividades marcadas acima serão sincronizadas
            automaticamente com o Google Agenda. Atividades desmarcadas não
            criarão eventos na sua agenda do Google.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const ApiKeyCard = () => {
  const notify = useNotify();
  const crmCtx = useOptionalCRM();

  const currentCrm = crmCtx?.currentCrm;
  const targetCrmId =
    currentCrm?.id ||
    localStorage.getItem(ACTIVE_CRM_STORAGE_KEY) ||
    DEFAULT_CRM_ID;

  // Existing key info fetched from api_keys table
  const [existingKey, setExistingKey] = useState<
    | {
        id: number;
        key_prefix: string;
        created_at: string;
      }
    | null
    | undefined
  >(undefined); // undefined = loading

  // The raw key to show once after generation
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load existing key on mount or when targetCrmId changes
  useEffect(() => {
    // Failsafe: se a conexão demorar mais de 4s, remove estado de carregamento
    const fallbackTimer = setTimeout(() => {
      setExistingKey((curr) => (curr === undefined ? null : curr));
    }, 4000);

    const load = async () => {
      try {
        const client = getSupabaseClient();
        const {
          data: { user },
        } = await client.auth.getUser();

        if (!user) {
          setExistingKey(null);
          return;
        }

        let query = client
          .from("api_keys")
          .select("id, key_prefix, created_at")
          .eq("user_id", user.id)
          .is("revoked_at", null);

        if (targetCrmId) {
          query = query.eq("crm_id", targetCrmId);
        }

        const { data } = await query
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        setExistingKey(data ?? null);
      } catch {
        setExistingKey(null);
      } finally {
        clearTimeout(fallbackTimer);
      }
    };
    load();

    return () => clearTimeout(fallbackTimer);
  }, [targetCrmId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const client = getSupabaseClient();
      const {
        data: { user },
        error: userError,
      } = await client.auth.getUser();

      if (userError || !user) {
        throw new Error("Usuário não autenticado no Supabase.");
      }

      // 1. Gera chave bruta segura
      const raw = generateRawKey();
      // 2. Calcula hash criptográfico SHA-256 (nunca salva a chave bruta no banco)
      const hash = await sha256Hex(raw);
      const prefix = raw.slice(0, 12); // "crm_" + 8 caracteres

      // 3. Remove chaves anteriores com o mesmo nome para este usuário neste CRM
      if (targetCrmId) {
        await client
          .from("api_keys")
          .delete()
          .eq("user_id", user.id)
          .eq("crm_id", targetCrmId)
          .eq("name", "Default");
      }

      // 4. Insere somente o hash criptográfico SHA-256 e o prefixo público vinculados ao CRM ativo
      let insertRes = await client
        .from("api_keys")
        .insert({
          user_id: user.id, // UUID real do auth.users
          crm_id: targetCrmId, // Garante integridade referencial com public.crms
          key_hash: hash, // SHA-256 hex digest
          key_prefix: prefix,
          name: "Default",
        })
        .select("id, key_prefix, created_at")
        .single();

      // Fallback: se houver conflito de constraint legada (ex: api_keys_user_id_unique sem crm_id no banco remoto),
      // remove qualquer registro anterior deste usuário com o mesmo nome e retenta a inserção
      if (insertRes.error && insertRes.error.message?.includes("unique")) {
        await client
          .from("api_keys")
          .delete()
          .eq("user_id", user.id)
          .eq("name", "Default");

        insertRes = await client
          .from("api_keys")
          .insert({
            user_id: user.id,
            crm_id: targetCrmId,
            key_hash: hash,
            key_prefix: prefix,
            name: "Default",
          })
          .select("id, key_prefix, created_at")
          .single();
      }

      if (insertRes.error) throw insertRes.error;

      setExistingKey(insertRes.data);
      setRawKey(raw);
      setShowRaw(true);
      notify(
        "API Key gerada! Apenas o hash SHA-256 é armazenado no banco. Copie agora.",
        { type: "success" },
      );
    } catch (err: any) {
      notify(err?.message || "Erro ao gerar API Key.", { type: "error" });
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async () => {
    if (!existingKey) return;
    setRevoking(true);
    try {
      const client = getSupabaseClient();
      await client
        .from("api_keys")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", existingKey.id);
      setExistingKey(null);
      setRawKey(null);
      notify("API Key revogada.", { type: "info" });
    } catch (err: any) {
      notify(err?.message || "Erro ao revogar API Key.", { type: "error" });
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = () => {
    if (!rawKey) return;
    navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isLoading = existingKey === undefined;

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-violet-500" />
              <h2 className="text-xl font-semibold text-muted-foreground">
                API Key
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {currentCrm?.name && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-muted text-muted-foreground border">
                  {currentCrm.name}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <ShieldCheck className="h-3.5 w-3.5" />
                Hash SHA-256
              </span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Use sua API Key para autenticar requisições ao CRM via REST. Por
            segurança,{" "}
            <strong className="text-foreground">
              apenas o hash criptográfico SHA-256 é gravado no banco
            </strong>{" "}
            — a chave original nunca fica armazenada em texto puro.{" "}
            <a
              href="#/api-playground"
              className="text-violet-500 hover:underline"
            >
              Testar no Playground →
            </a>
          </p>

          {isLoading && (
            <p className="text-xs text-muted-foreground animate-pulse">
              Carregando...
            </p>
          )}

          {!isLoading && existingKey && !rawKey && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Chave ativa</p>
                <p className="font-mono text-sm tracking-wider">
                  {existingKey.key_prefix}••••••••••••••••
                </p>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRevoke}
                      disabled={revoking}
                      className="text-destructive hover:text-destructive"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Revogar
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Revoga a chave atual. Você poderá gerar uma nova.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {rawKey && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-950/20 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-emerald-400 mb-0.5">
                    Copie agora — esta chave não será exibida novamente
                  </p>
                  <p className="font-mono text-sm break-all">
                    {showRaw ? rawKey : rawKey.replace(/./g, "•")}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRaw((v) => !v)}
                    className="h-8 w-8 p-0"
                  >
                    {showRaw ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-8 w-8 p-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!isLoading && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1"
            >
              <RefreshCw
                className={`h-4 w-4 ${generating ? "animate-spin" : ""}`}
              />
              {existingKey
                ? "Gerar nova chave (revoga a atual)"
                : "Gerar API Key"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

ProfilePage.path = "/profile";
